/**
 * The guided demo over the real flow canvas on /features: a cursor edits a section, drags a card and tests the bot
 * in a live chat, while the canvas's own trace, routing and camera respond. On phones, the whole map stays above the chat.
 */
export function featuresCanvasDemo(scenes) {
    return {
        canvas: null,
        canvasShown: false,
        cursorX: 0,
        cursorY: 0,
        cursorShown: false,
        cursorInstant: false,
        pressing: false,
        rippleX: 0,
        rippleY: 0,
        chatOpen: false,
        typing: false,
        messages: [],
        onScreen: true,
        stopped: false,
        destroyed: false,
        restarting: false,
        running: null,
        lastWidth: 0,
        resizeTimer: null,
        observer: null,
        resizeObserver: null,
        nextMessageId: 0,

        get usesCursor() { return window.matchMedia('(width >= 48rem)').matches; },

        async wait(milliseconds) {
            let left = milliseconds;
            while (left > 0 && !this.stopped) {
                await new Promise((resolve) => setTimeout(resolve, Math.min(left, 100)));
                if (this.onScreen && !document.hidden) left -= 100;
            }
        },
        send(name, detail) {
            window.dispatchEvent(new CustomEvent(name, { detail: { botId: 'features-demo', ...detail } }));
        },
        /** Runs `step` with a 0–1 progress on a timer, so it finishes even if the browser stops drawing frames. */
        animate(duration, step) {
            return new Promise((resolve) => {
                const start = performance.now();
                const timer = setInterval(() => {
                    const progress = Math.min((performance.now() - start) / duration, 1);
                    step(progress * progress * (3 - 2 * progress));
                    if (progress === 1 || this.stopped) {
                        clearInterval(timer);
                        resolve();
                    }
                }, 16);
            });
        },
        pointOn(target, across = 0.5, down = 0.5) {
            const box = target.getBoundingClientRect();
            const frame = this.$el.getBoundingClientRect();

            return { x: box.left - frame.left + box.width * across, y: box.top - frame.top + box.height * down };
        },
        async moveTo(selector, across = 0.5) {
            const target = this.$el.querySelector(selector);
            if (!target) return null;
            const point = this.pointOn(target, across);
            this.cursorInstant = false;
            this.cursorX = point.x;
            this.cursorY = point.y;
            this.cursorShown = true;
            await this.wait(900);

            return target;
        },
        async click() {
            this.pressing = true;
            await this.wait(100);
            this.rippleX = this.cursorX;
            this.rippleY = this.cursorY;
            this.$refs.ripple.animate(
                [{ transform: 'translate(-50%, -50%) scale(0.2)', opacity: 0.45 }, { transform: 'translate(-50%, -50%) scale(1)', opacity: 0 }],
                { duration: 520, easing: 'cubic-bezier(0.22, 1, 0.36, 1)' },
            );
            this.pressing = false;
            await this.wait(200);
        },

        /** Act one: click a card's message and type a new one, straight into the real card. */
        async editMessage(text) {
            const body = `[data-flow-node='1'] .bg-zinc-50 p`;
            const target = await this.moveTo(body, 0.7);
            if (!target) return;
            await this.click();
            this.canvas.selected = '1';
            await this.wait(300);
            for (let length = target.textContent.length; length >= 0; length -= 2) {
                target.textContent = target.textContent.slice(0, length);
                await this.wait(100);
            }
            for (let length = 1; length <= text.length; length++) {
                target.textContent = text.slice(0, length);
                await this.wait(100);
            }
            await this.wait(500);
            this.canvas.selected = '';
        },

        /** Act two: drag a card; its connections reroute while it moves, as they do in the dashboard. */
        async dragCard(flowId, distanceY) {
            const element = this.$el.querySelector(`[data-flow-node='${flowId}']`);
            const node = this.canvas.nodes.find((item) => item.id === String(flowId));
            if (!element || !node) return;
            await this.moveTo(`[data-flow-node='${flowId}'] .flow-node-header`, 0.6);
            this.pressing = true;
            await this.wait(150);
            element.classList.add('feature-demo-lifted');
            this.cursorInstant = true;
            const startY = node.y;
            const startCursorY = this.cursorY;
            await this.animate(800, (progress) => {
                node.y = startY + distanceY * progress;
                this.cursorY = startCursorY + distanceY * this.canvas.scale * progress;
            });
            element.classList.remove('feature-demo-lifted');
            this.pressing = false;
            this.cursorInstant = false;
            await this.wait(900);

            return () => { node.y = startY; };
        },

        /** The real connection fills in thin green from the card that was used to the next one, then the trace takes over. */
        async sendAlong(edgeId) {
            const edge = this.canvas.edges.find((item) => item.id === edgeId);
            const svg = this.$el.querySelector('[x-ref="viewport"] svg');
            if (!edge?.path || !svg) return;
            const line = document.createElementNS('http://www.w3.org/2000/svg', 'path');
            line.setAttribute('d', edge.path);
            line.setAttribute('fill', 'none');
            line.setAttribute('stroke-linecap', 'round');
            line.setAttribute('stroke-width', String(1.5 / this.canvas.scale));
            line.setAttribute('class', 'stroke-emerald-600 dark:stroke-emerald-400');
            svg.append(line);
            const length = line.getTotalLength();
            line.setAttribute('stroke-dasharray', String(length));
            await this.animate(650, (progress) => {
                line.setAttribute('stroke-dashoffset', String(length * (1 - progress)));
            });
            setTimeout(() => line.remove(), 350);
        },

        addMessage(message) {
            this.messages.push({ id: this.nextMessageId++, buttons: [], picked: null, ...message });
        },
        async botSays(text, buttons = []) {
            this.typing = true;
            await this.wait(650);
            this.typing = false;
            this.addMessage({ from: 'bot', text, buttons });
            await this.wait(450);
        },
        /** Makes room for the chat: the map shrinks into the space beside it, using the canvas's own camera. */
        openChat() {
            this.chatOpen = true;
            const viewport = this.$el.querySelector('[x-ref="viewport"]');
            const bounds = this.canvas.contentBounds;
            const roomX = viewport.clientWidth - 296;
            const width = bounds.right - bounds.left;
            const height = bounds.bottom - bounds.top;
            const scale = Math.min(1, (roomX - 48) / width, (viewport.clientHeight - 48) / height);
            this.canvas.setCamera({
                scale,
                panX: viewport.clientWidth - 24 - bounds.right * scale,
                panY: (viewport.clientHeight - height * scale) / 2 - bounds.top * scale,
            }, true);
        },
        /** Act three: test the bot; every tap in the chat lights up the map. */
        async testInChat(scene) {
            if (this.usesCursor) {
                if (await this.moveTo('[data-demo-preview]')) await this.click();
                this.openChat();
                await this.wait(900);
            } else {
                this.chatOpen = true;
                await this.wait(300);
            }
            const trace = [{ flowId: 1, edgeId: null }];
            this.send('bot-preview-trace', { flowId: 1, steps: trace });
            for (const step of scene.steps) {
                await this.botSays(step.bot, step.buttons);
                const button = await this.moveTo(`[data-chat-button='${step.press}']`);
                if (button) await this.click();
                this.messages.at(-1).picked = step.press;
                this.addMessage({ from: 'user', text: step.press });
                await this.wait(300);
                await this.sendAlong(step.edgeId);
                trace.push({ flowId: step.flowId, edgeId: step.edgeId });
                this.send('bot-preview-trace', { flowId: step.flowId, steps: [...trace] });
                await this.wait(450);
            }
            await this.botSays(scene.final);
            this.cursorShown = false;
            await this.wait(2400);
        },
        async tidyUp(restoreCard, restoreText) {
            this.chatOpen = false;
            this.send('bot-preview-trace-cleared', {});
            restoreCard?.();
            restoreText?.();
            await this.wait(250);
            this.canvas.fit(true);
            await this.wait(1100);
            this.messages = [];
        },
        async playScene(scene) {
            const body = this.$el.querySelector(`[data-flow-node='1'] .bg-zinc-50 p`);
            const original = body?.textContent;
            await this.editMessage(scene.steps[0].bot);
            const restoreCard = await this.dragCard(scene.dragFlowId, scene.dragY);
            await this.testInChat(scene);
            await this.tidyUp(restoreCard, () => { if (body) body.textContent = original; });
        },
        /** Phones: the whole map stays in view above the chat, and every tap fills in the next connection. */
        fitAboveChat() {
            const viewport = this.$el.querySelector('[x-ref="viewport"]');
            const bounds = this.canvas.contentBounds;
            const roomY = 192;
            const width = bounds.right - bounds.left;
            const height = bounds.bottom - bounds.top;
            const scale = Math.min(1, (viewport.clientWidth - 16) / width, (roomY - 16) / height);
            this.canvas.setCamera({
                scale,
                panX: (viewport.clientWidth - width * scale) / 2 - bounds.left * scale,
                panY: (roomY - height * scale) / 2 - bounds.top * scale,
            }, false);
        },
        async playOnPhone(scene) {
            this.fitAboveChat();
            await this.testInChat(scene);
            this.chatOpen = false;
            this.send('bot-preview-trace-cleared', {});
            await this.wait(500);
            this.messages = [];
        },
        async run() {
            // The canvas starts after this component, so its data only exists a moment later.
            const section = this.$el.querySelector('section');
            for (let tries = 0; tries < 100 && !this.stopped; tries++) {
                this.canvas = window.Alpine.$data(section);
                if (this.canvas.ready === true) break;
                await new Promise((resolve) => setTimeout(resolve, 20));
            }
            await new Promise((resolve) => setTimeout(resolve, 60));
            if (this.stopped) return;
            this.canvas.followPreview = false;
            this.placeCamera();
            this.lastWidth = this.$el.clientWidth;
            this.canvasShown = true;
            this.running = this.play();
        },
        placeCamera() {
            if (this.usesCursor) this.canvas.fit(false);
            else this.fitAboveChat();
        },
        /** The window changed width mid-demo: the chat and the camera belong to the old layout, so start over in the new one. */
        async restart() {
            if (this.restarting || this.$el.clientWidth === this.lastWidth) return;
            this.restarting = true;
            this.canvasShown = false;
            this.stopped = true;
            await this.running;
            if (this.destroyed) return;
            this.stopped = false;
            this.messages = [];
            this.typing = false;
            this.chatOpen = false;
            this.cursorShown = false;
            this.pressing = false;
            this.send('bot-preview-trace-cleared', {});
            await new Promise((resolve) => setTimeout(resolve, 320));
            if (this.destroyed) return;
            this.lastWidth = this.$el.clientWidth;
            this.placeCamera();
            this.canvasShown = true;
            this.restarting = false;
            this.running = this.play();
        },
        async play() {
            await this.wait(1000);
            if (this.stopped) return;
            if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
                this.canvas.followPreview = false;
                this.send('bot-preview-trace', { flowId: 4, steps: [{ flowId: 1, edgeId: null }, ...scenes[0].steps.map(({ flowId, edgeId }) => ({ flowId, edgeId }))] });

                return;
            }
            for (let round = 0; !this.stopped; round++) {
                const scene = scenes[round % scenes.length];
                if (this.usesCursor) await this.playScene(scene);
                else await this.playOnPhone(scene);
            }
        },
        init() {
            this.observer = new IntersectionObserver(([entry]) => { this.onScreen = entry.isIntersecting; });
            this.observer.observe(this.$el);
            this.resizeObserver = new ResizeObserver(() => {
                if (!this.running) return;
                clearTimeout(this.resizeTimer);
                this.resizeTimer = setTimeout(() => this.restart(), 250);
            });
            this.resizeObserver.observe(this.$el);
            this.run();
        },
        destroy() {
            this.destroyed = true;
            this.stopped = true;
            clearTimeout(this.resizeTimer);
            this.observer?.disconnect();
            this.resizeObserver?.disconnect();
        },
    };
}
